/**
 * ClosedThreadNotice — what a bell row says once its thread is closed
 * (2026-09-21, owner directive: "include the bell now").
 *
 * The mentions tab already retires a row in place. The bell has the same dead
 * end in three places — NotificationsBell, MobileNotifications and the
 * /notifications page — so the words, the presentation and the behaviour live
 * here once rather than three times.
 *
 * WHY A REAL BUTTON, AND WHY THE ROW IS NOT ONE. A bell row is a `div` wearing
 * `role="button"`. Retiring it by adding `aria-disabled` would leave a control
 * that is announced as disabled and focusable by nobody — and the whole
 * explanation depends on being able to reach it. So the row drops its role
 * entirely and this component carries the one real, focusable control. (The
 * mentions tab keeps `aria-disabled` because its row genuinely IS a
 * `<button>`; same principle, different starting shape.)
 *
 * ONE TOOLTIP, NEVER TWO. No native `title` anywhere: this tree tried a title
 * beside a portaled card and reversed it after its own adversarial review,
 * because the two stacked. The full sentence is in the control's accessible
 * NAME — leading with the retired fact, because a description is announced
 * after the name and is routinely suppressed by verbosity settings — and the
 * portal covers sighted hover and focus. The card is `aria-hidden`.
 *
 * TWO REVEALS, ONE SENTENCE:
 *  · `hover` (the bell and the mobile sheet, where a row is a few lines high):
 *    the card on hover/focus, and pressing the control opens the same sentence
 *    IN FLOW — which is the answer on a touch device, where hover never fires.
 *  · `always` (/notifications, which has room): the sentence is simply printed.
 *    Hiding a fact behind a hover on a page with space is worse.
 */
import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { HoverCardPortal } from '../GlobalChatSystem/HoverCardPortal';
import { APP_TOOLTIP_Z_INDEX } from '../../styles/layers';
import type { PickerAnchor } from '../../services/messageReactions/pickerPlacement';
import type { ThreadClosure } from '../../services/groupThreads/threadClosure';
import {
  threadClosurePill,
  threadClosureRowName,
  threadClosureSentence,
} from '../../services/groupThreads/threadClosureCopy';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';

const Wrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`;

/**
 * Neutral, never gold. Gold reads as actionable in this palette, and this
 * control leads nowhere — it only explains. Neutral also keeps the file out of
 * check-gold-as-text's baseline, which is keyed by ORDINAL and renumbers every
 * later row when one is inserted.
 */
const Pill = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.14);
  background: rgba(0, 0, 0, 0.04);
  color: #444;
  font: inherit;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;

  /* THE BOX IS DELIBERATE, BOTH AXES, OR THE ROW RE-FLOWS.
     GlobalStyles gives every <button> min-height: 44px AND min-width: 44px at
     specificity (0,0,1): a styled class beats it on padding but NOT on a
     property it never declares, so an undeclared pill renders 44px tall and
     roughly halves how many rows fit the bell's 360px-tall, 320px-wide
     dropdown. The doubled class outranks the element rule without touching a
     rule that is right about real controls. Pin BOTH axes — pinning one is the
     ellipse defect check-tap-target-geometry.mjs exists for — and give a thumb
     the floor back under (pointer: coarse), where this pill IS the tap target
     that reveals the sentence. */
  && {
    min-height: 22px;
    min-width: 0;
  }

  @media (pointer: coarse) {
    && {
      min-height: 44px;
      min-width: 44px;
    }
  }

  &:hover {
    background: rgba(0, 0, 0, 0.07);
  }

  &:focus-visible {
    outline: 2px solid var(--brand-gold-hover);
    outline-offset: 2px;
  }
`;

const Reason = styled.span`
  color: #444;
  font-size: 0.8rem;
  line-height: 1.35;
`;

export type ClosedThreadNoticeReveal = 'hover' | 'always';

export interface ClosedThreadNoticeProps {
  closure: ThreadClosure;
  /** What the row would otherwise have said about itself — the row's own title. */
  description: string;
  /** A stable id for this row, used only for the test markers. */
  rowId: string;
  reveal?: ClosedThreadNoticeReveal;
  /** Test seam; defaults to the app's own resolver. */
  lang?: 'en' | 'es';
}

export function ClosedThreadNotice({
  closure,
  description,
  rowId,
  reveal = 'hover',
  lang,
}: ClosedThreadNoticeProps): React.ReactElement {
  const language = lang ?? getUserUiLanguage();
  const sentence = threadClosureSentence(closure, language);
  const pill = threadClosurePill(closure, language);
  const name = threadClosureRowName(closure, description, language);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<PickerAnchor | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const show = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchor({ x: r.left + r.width / 2, y: r.top, width: r.width, height: r.height });
  }, []);
  const hide = useCallback(() => setAnchor(null), []);

  if (reveal === 'always') {
    return (
      <Wrap data-closed-notice={rowId}>
        <Pill as="span" aria-hidden="true">{pill}</Pill>
        <Reason data-closed-notice-reason={rowId}>{sentence}</Reason>
      </Wrap>
    );
  }

  return (
    <Wrap data-closed-notice={rowId}>
      <Pill
        ref={buttonRef}
        type="button"
        data-closed-notice-why={rowId}
        aria-label={name}
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setAnchor(null); setOpen((v) => !v); }}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {pill}
      </Pill>
      {open && <Reason data-closed-notice-reason={rowId}>{sentence}</Reason>}
      {/* Never both: the in-flow line already says it, and a card floating over
          the words it duplicates is noise. */}
      {!open && anchor && (
        <HoverCardPortal
          anchor={anchor}
          bounds={null}
          zIndex={APP_TOOLTIP_Z_INDEX}
          markers={{ 'data-closed-notice-card': rowId }}
          placeKey={sentence}
        >
          {sentence}
        </HoverCardPortal>
      )}
    </Wrap>
  );
}

export default ClosedThreadNotice;
